import {
    useState,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useRef,
} from "react";
import { BillViewOptions, BillViewOptionGroup } from "@common/types/bill";
import { useBillsPageFiltersCache } from "@hooks/view/useBillsPageFiltersCache";
import { useBillsPageViewOptionsCache } from "@hooks/view/useBillsPageViewOptionsCache";
import { defaultBillStatusFilter } from "@components/pageHeader/filters/lib";
import { normalizeBillStatusFilters } from "@helpers/billStatusFilter";
import { useBillStats } from "@api/bill.api";
import { JsonParam, StringParam, useQueryParams } from "use-query-params";
import { z } from "zod";
import {
    BillsViewContext,
    BillViewFilters,
    toBillApiFilters,
} from "./BillsViewContext";

const billUrlFiltersSchema = z.object({
    billIds: z.array(z.string()).optional(),
    entityIds: z.array(z.number()).optional(),
    counterpartiesIds: z.array(z.string()).optional(),
});

export const BillsViewProvider: React.FC<PropsWithChildren> = ({
    children,
}) => {
    const [cachedFilters, setCachedFilters] = useBillsPageFiltersCache();
    const [cachedViewOptions, setCachedViewOptions] =
        useBillsPageViewOptionsCache();

    const [
        { filters: urlFilters, scheduleTemplateId: urlScheduleTemplateId },
        setQueryParams,
    ] = useQueryParams({
        filters: JsonParam,
        scheduleTemplateId: StringParam,
    });

    const parsedUrlFilters = useMemo(() => {
        if (!urlFilters) {
            return undefined;
        }
        const { data, success } = billUrlFiltersSchema.safeParse(urlFilters);
        return success ? data : undefined;
    }, [urlFilters]);

    const initialIdsRef = useRef(parsedUrlFilters?.billIds);
    const initialIds = initialIdsRef.current;
    const hasUrlFilters = Boolean(
        parsedUrlFilters?.billIds?.length ??
        parsedUrlFilters?.entityIds?.length ??
        parsedUrlFilters?.counterpartiesIds?.length,
    );

    const [storedFilters, setStoredFilters] = useState<
        BillViewFilters | undefined
    >(() => {
        if (hasUrlFilters && parsedUrlFilters) {
            return {
                billStatusFilter: defaultBillStatusFilter,
                ...(parsedUrlFilters.billIds && {
                    billIds: parsedUrlFilters.billIds,
                }),
                ...(parsedUrlFilters.entityIds && {
                    entityIds: parsedUrlFilters.entityIds,
                }),
                ...(parsedUrlFilters.counterpartiesIds && {
                    counterpartiesIds: parsedUrlFilters.counterpartiesIds,
                }),
            };
        }
        return normalizeBillStatusFilters({
            billStatusFilter: defaultBillStatusFilter,
            ...cachedFilters,
        });
    });

    const setFilters = useCallback<
        Dispatch<SetStateAction<BillViewFilters | undefined>>
    >((action) => {
        setStoredFilters((previous) => {
            const next =
                typeof action === "function" ? action(previous) : action;
            return next && normalizeBillStatusFilters(next);
        });
    }, []);

    const clearUrlFiltersOnMount = useEffectEvent(() => {
        if (hasUrlFilters) {
            setQueryParams({ filters: undefined }, "replaceIn");
        }
    });
    useEffect(() => {
        clearUrlFiltersOnMount();
    }, []);

    const [viewOption, setViewOption] = useState<BillViewOptions>(() => {
        // "Add bills" on a policy lands on whole bills, where its column is.
        if (urlScheduleTemplateId) {
            return { groupBy: BillViewOptionGroup.BILL };
        }
        if (initialIds?.length) {
            return {
                groupBy: BillViewOptionGroup.LINE_ITEMS,
            };
        }
        return (
            cachedViewOptions ?? {
                groupBy: BillViewOptionGroup.LINE_ITEMS,
            }
        );
    });

    useEffect(() => {
        setCachedFilters(toBillApiFilters(storedFilters));
    }, [storedFilters, setCachedFilters]);

    useEffect(() => {
        setCachedViewOptions(viewOption);
    }, [viewOption, setCachedViewOptions]);

    const { data: billStats } = useBillStats({
        filters: toBillApiFilters(storedFilters),
    });

    const contextValue = useMemo(
        () => ({
            filters: storedFilters,
            setFilters,
            viewOption,
            setViewOption,
            billStats: billStats?.data,
            initialIds,
        }),
        [
            storedFilters,
            setFilters,
            viewOption,
            setViewOption,
            billStats?.data,
            initialIds,
        ],
    );

    return (
        <BillsViewContext.Provider value={contextValue}>
            {children}
        </BillsViewContext.Provider>
    );
};
